import React, { useEffect, useMemo, useRef, useState } from "react";
import { arc as d3arc } from "d3-shape";
import { CHART_CONFIG } from "./chart/config";
import { SCENE_SIZE, buildScene } from "./chart/layout";
import { hitTest, FONT_STACK } from "./chart/geometry";
import { useHoverTween } from "./chart/useHoverTween";

/**
 * Generic drill-down donut. Takes ANY tree of
 * `{ children: [{ id, label, value?, color?, children? }] }` — ring count
 * follows the tree's depth — and renders it as a click-to-drill radial chart;
 * it knows nothing about what the nodes mean. Parents adapt their dataset
 * into this shape and decide what a leaf click does via `onLeafClick(node)`
 * (any extra properties the adapter left on the node ride along). `config`
 * overrides individual CHART_CONFIG values; `ariaLabel` names the svg. The
 * chart fills its parent — size the wrapping element.
 *
 * Owns hover/selection state, pointer handling and the scene build; renders
 * the scene as plain SVG. Hit-testing runs in scene coordinates against the
 * arcs (not DOM targets), so the gap-slack and z-order behaviour come from
 * the shared geometry, and the svg needs no per-element listeners.
 *
 * Clicking a segment opens it and closes anything deeper; clicking the open
 * segment again, or the background, walks back out. Every ring runs its own
 * tween, so closing several at once just plays them all at once. Leaves have
 * nothing to drill into — their click is delegated up.
 */

const shape = d3arc()
  .innerRadius((d) => d.r0)
  .outerRadius((d) => d.r1)
  .startAngle((d) => d.a0)
  .endAngle((d) => d.a1)
  .cornerRadius((d) => d.cornerRadius);

const HATCH_ID = "drilldown-donut-hatch";

export const DrilldownDonut = ({ data, onLeafClick, activePath: controlledActivePath, onActivePathChange, config: configOverrides, ariaLabel = "Hierarchical data distribution" }) => {
  const boxRef = useRef(null);

  // Ring count follows the data's depth, so any tree shape just works;
  // explicit overrides (including ringCount) win over both.
  const config = useMemo(() => {
    const depthOf = (n) => (n.children?.length ? 1 + Math.max(...n.children.map(depthOf)) : 0);
    return { ...CHART_CONFIG, ringCount: depthOf(data), ...configOverrides };
  }, [data, configOverrides]);

  // id → node, so a hit-tested arc can hand its node back to the parent.
  const nodeById = useMemo(() => {
    const m = new Map();
    const walk = (nodes) =>
      nodes.forEach((n) => {
        m.set(n.id, n);
        if (n.children) walk(n.children);
      });
    walk(data.children);
    return m;
  }, [data]);

  // The container is free-form; the svg letterboxes the fixed-aspect scene
  // inside it (preserveAspectRatio "meet"). Track both dimensions so the
  // displayed-px-per-scene-px scale — and the pointer mapping below — follow
  // the fitted scene, not the raw box.
  const [box, setBox] = useState(null);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() =>
      setBox({ w: el.clientWidth, h: el.clientHeight })
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const scale = box
    ? Math.min(box.w / SCENE_SIZE.width, box.h / SCENE_SIZE.height)
    : 0;

  const [hovered, setHovered] = useState(null);
  // Drill state — uncontrolled by default; a parent that passes `activePath`
  // owns it (e.g. to drive a breadcrumb that can jump levels), and every
  // change flows out through onActivePathChange either way.
  const [internalPath, setInternalPath] = useState([]);
  const onActivePathChangeRef = useRef(onActivePathChange);
  onActivePathChangeRef.current = onActivePathChange;
  const isControlled = controlledActivePath !== undefined;
  const activePath = isControlled ? controlledActivePath : internalPath;
  const updatePath = (next) => {
    if (!isControlled) setInternalPath(next);
    onActivePathChangeRef.current?.(next);
  };

  // Selection can dangle after a refetch reshapes the tree — clear it rather
  // than leave the chart drilled into a segment that no longer exists.
  useEffect(() => {
    if (!activePath.every((id) => nodeById.has(id))) updatePath([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeById, activePath]);

  const hoverTween = useHoverTween(hovered, config.transition);
  const revealTween = useHoverTween(activePath, config.transition);
  const scene = useMemo(
    () => buildScene(data, scale || 1, hoverTween, revealTween, activePath, config),
    [data, scale, hoverTween, revealTween, activePath, config]
  );

  const segmentAt = (e) => {
    const el = boxRef.current;
    if (!el || !scale) return null;
    const rect = el.getBoundingClientRect();
    // Undo the letterbox: the scene sits centred in the box at `scale`.
    const ox = (rect.width - SCENE_SIZE.width * scale) / 2;
    const oy = (rect.height - SCENE_SIZE.height * scale) / 2;
    const x = (e.clientX - rect.left - ox) / scale;
    const y = (e.clientY - rect.top - oy) / scale;
    return hitTest(scene, x, y);
  };

  const onClick = (e) => {
    const arc = segmentAt(e);
    if (!arc) return updatePath([]);
    if (arc.selectable) {
      updatePath(
        activePath[arc.depth] === arc.id
          ? activePath.slice(0, arc.depth)
          : [...activePath.slice(0, arc.depth), arc.id]
      );
      return;
    }
    const node = nodeById.get(arc.id);
    if (node) onLeafClick?.(node);
  };

  return (
    <div
      ref={boxRef}
      onPointerMove={(e) => setHovered(segmentAt(e)?.id ?? null)}
      onPointerLeave={() => setHovered(null)}
      onClick={onClick}
      style={{
        // Fills whatever box the parent sizes; the scene letterboxes inside.
        width: "100%",
        height: "100%",
        cursor: hovered ? "pointer" : "default",
      }}
    >
      {scale > 0 && (
        <svg
          viewBox={`0 0 ${scene.width} ${scene.height}`}
          style={{ display: "block", width: "100%", height: "100%" }}
          role="img"
          aria-label={ariaLabel}
        >
          <defs>
            <pattern
              id={HATCH_ID}
              width={scene.hatch.tile}
              height={scene.hatch.tile}
              patternUnits="userSpaceOnUse"
            >
              <rect width={scene.hatch.tile} height={scene.hatch.tile} fill={scene.hatch.bg} />
              <path
                d={`M${-scene.hatch.tile},${scene.hatch.tile} L0,0 M0,${scene.hatch.tile} L${scene.hatch.tile},0 M${scene.hatch.tile},${scene.hatch.tile} L${scene.hatch.tile * 2},0`}
                stroke={scene.hatch.line}
                strokeWidth={1}
              />
            </pattern>
          </defs>

          <g>
            {scene.lines.map((l, i) => (
              <polyline
                key={i}
                points={l.points.map((p) => p.join(",")).join(" ")}
                fill="none"
                stroke={l.stroke}
                strokeWidth={l.width}
                strokeLinecap="round"
                strokeDasharray={l.dash ? l.dash.join(" ") : undefined}
                opacity={l.opacity ?? 1}
              />
            ))}
          </g>

          <g transform={`translate(${scene.cx},${scene.cy})`}>
            {scene.arcs.map((a) => (
              <path
                key={a.id}
                d={shape(a)}
                fill={a.hatch ? `url(#${HATCH_ID})` : a.fill ?? "none"}
                stroke={a.stroke ?? "none"}
                strokeWidth={a.strokeWidth ?? 0}
                opacity={a.opacity ?? 1}
              />
            ))}
          </g>

          {/* On-band curved names for drilled segments — their leader labels
              hand over to their children, so the identity moves onto the arc. */}
          <g transform={`translate(${scene.cx},${scene.cy})`}>
            {scene.arcLabels.map((al, i) => {
              const pathId = `drilldown-donut-arclbl-${i}`;
              return (
                <g key={i} opacity={al.opacity}>
                  <defs>
                    <path
                      id={pathId}
                      d={`M ${al.sx} ${al.sy} A ${al.r} ${al.r} 0 ${al.largeArc} ${al.sweep} ${al.ex} ${al.ey}`}
                      fill="none"
                    />
                  </defs>
                  <text
                    fontFamily={FONT_STACK}
                    fontSize={al.size}
                    fontWeight={al.weight}
                    letterSpacing={al.tracking}
                    fill={al.color}
                  >
                    <textPath href={`#${pathId}`} startOffset="50%" textAnchor="middle">
                      {al.count && <tspan fontWeight={al.countWeight}>{al.count}</tspan>}
                      {al.text}
                    </textPath>
                  </text>
                </g>
              );
            })}
          </g>

          <g>
            {scene.labels.map((l, i) => (
              <text
                key={i}
                textAnchor={l.align === "end" ? "end" : "start"}
                fontFamily={FONT_STACK}
                fontSize={l.size}
                fontWeight={l.weight}
                letterSpacing={l.tracking}
                fill={l.color}
                opacity={l.opacity ?? 1}
              >
                {/* l.y is the last line's baseline; earlier lines stack above.
                    The count leads the first line in its own heavier run. */}
                {l.lines.map((line, j) => (
                  <tspan key={j} x={l.x} y={l.y - (l.lines.length - 1 - j) * l.lineHeight}>
                    {j === 0 && l.count && <tspan fontWeight={l.countWeight}>{l.count}</tspan>}
                    {line}
                  </tspan>
                ))}
              </text>
            ))}
          </g>
        </svg>
      )}
    </div>
  );
};
